"use client";

import { useState, useMemo } from "react";
import { BookOpen, Mic, Music } from "lucide-react";
import { useTranslations } from "@/lib/use-translations";
import { useWordState } from "@/lib/use-word-state";
import { buildWordModules, type WordModule } from "@/lib/word-modules";
import type { TopicVocabulary } from "@/lib/dialog-data";
import type { LocaleCode } from "@/lib/api/client";
import { WordQuiz } from "./word-quiz";
import { SpellingPractice } from "./spelling-practice";
import { KaraokePractice } from "./karaoke-practice";
import { ModuleSelector } from "./module-selector";

type PracticeMode = "select" | "words_modules" | "spelling_modules" | "words" | "spelling" | "karaoke";

interface PracticeSectionProps {
  vocabulary: TopicVocabulary;
  language: LocaleCode;
  topicTitle?: string;
  topicDescription?: string;
}

const modes = [
  {
    id: "words" as const,
    icon: BookOpen,
    labelKey: "practiceWords" as const,
    descKey: "practiceWordsDesc" as const,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
  },
  {
    id: "spelling" as const,
    icon: Mic,
    labelKey: "practiceSpelling" as const,
    descKey: "practiceSpellingDesc" as const,
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
  },
  {
    id: "karaoke" as const,
    icon: Music,
    labelKey: "practiceKaraoke" as const,
    descKey: "practiceKaraokeDesc" as const,
    color: "text-purple-500",
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
  },
];

export function PracticeSection({ vocabulary, language, topicTitle, topicDescription }: PracticeSectionProps) {
  const [mode, setMode] = useState<PracticeMode>("select");
  const [selectedModule, setSelectedModule] = useState<WordModule | null>(null);
  const t = useTranslations();
  const wordStateHook = useWordState();

  // Build modules from vocabulary words
  const wordModules = useMemo(
    () => buildWordModules(vocabulary.words, wordStateHook.state),
    [vocabulary.words, wordStateHook.state]
  );

  // Count known words for display
  const knownCount = useMemo(() => {
    let count = 0;
    for (const w of vocabulary.words) {
      if (wordStateHook.isKnown(w.baseForm)) count++;
    }
    return count;
  }, [vocabulary.words, wordStateHook]);

  const handleBack = () => {
    setMode("select");
    setSelectedModule(null);
  };

  const handleModeSelect = (modeId: "words" | "spelling" | "karaoke") => {
    if (modeId === "karaoke") {
      setMode("karaoke");
      return;
    }

    // Check if we need module selector (>12 words and more than one module)
    if (wordModules.length > 1) {
      setMode(modeId === "words" ? "words_modules" : "spelling_modules");
    } else {
      // Single module or <= 12 words — go straight to practice
      if (wordModules.length === 1) {
        setSelectedModule(wordModules[0]!);
      }
      setMode(modeId);
    }
  };

  const handleModuleSelect = (mod: WordModule) => {
    setSelectedModule(mod);
    setMode(mode === "words_modules" ? "words" : "spelling");
  };

  // Module selector for words
  if (mode === "words_modules") {
    return (
      <ModuleSelector
        modules={wordModules}
        onSelect={handleModuleSelect}
        onBack={handleBack}
        knownCount={knownCount}
      />
    );
  }

  // Module selector for spelling
  if (mode === "spelling_modules") {
    return (
      <ModuleSelector
        modules={wordModules}
        onSelect={handleModuleSelect}
        onBack={handleBack}
        knownCount={knownCount}
      />
    );
  }

  if (mode === "words") {
    return (
      <WordQuiz
        vocabulary={vocabulary}
        language={language}
        topicTitle={topicTitle}
        topicDescription={topicDescription}
        onBack={handleBack}
        words={selectedModule?.words}
        wordState={wordStateHook}
      />
    );
  }

  if (mode === "spelling") {
    return <SpellingPractice vocabulary={selectedModule ? { ...vocabulary, words: selectedModule.words } : vocabulary} language={language} onBack={handleBack} />;
  }

  if (mode === "karaoke") {
    return <KaraokePractice vocabulary={vocabulary} language={language} onBack={handleBack} />;
  }

  return (
    <div className="p-4 space-y-4 max-w-lg mx-auto">
      <h2 className="text-lg font-semibold text-foreground">
        {t.learning.practiceTab || "Practice"}
      </h2>

      <div className="space-y-3">
        {modes.map((m) => {
          const Icon = m.icon;
          const hasContent =
            m.id === "karaoke"
              ? vocabulary.expressions.length > 0
              : vocabulary.words.length > 0;

          return (
            <button
              key={m.id}
              onClick={() => hasContent && handleModeSelect(m.id)}
              disabled={!hasContent}
              className={`
                w-full flex items-center gap-4 p-4 rounded-xl border transition-all
                ${hasContent
                  ? `${m.border} hover:shadow-md hover:scale-[1.01] active:scale-[0.99] cursor-pointer`
                  : "border-border/30 opacity-50 cursor-not-allowed"
                }
                bg-card
              `}
            >
              <div className={`w-12 h-12 rounded-lg ${m.bg} flex items-center justify-center shrink-0`}>
                <Icon className={`w-6 h-6 ${m.color}`} />
              </div>
              <div className="text-left">
                <div className="font-medium text-foreground">
                  {t.learning[m.labelKey] || m.id}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t.learning[m.descKey] || ""}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
